import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { cx, MarkerIcon } from "../shared/markers";
import { PHONE_MAX_WIDTH, sheetMinHeight } from "./camera";
import HostelPanel from "./HostelPanel";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import InfoLinks from "./Info";
import { planningUrl, placePlanningUrl } from "./links";
import { fromPrice } from "./offers";
import { sisterOf } from "./SisterCard";
import { shouldCloseSheet } from "./swipe";
import type { PartnerMapAction } from "./track";
import type { PartnerMapContent } from "./types";
import { VenuePanel, SisterPanel } from "./VenuePanel";
import { bikeMinutes, hostelLook, isBike, walkLabel, walkMinutes, type MappedPlace } from "./walk";
import { eventsOn, signupUrl } from "./weekProgram";
import styles from "./PartnerMap.module.scss";

/** What the detail sheet is open on. */
export type Selection = { kind: "base" } | { kind: "place"; slug: string } | { kind: "event"; id: string };

interface SheetProps {
	content: PartnerMapContent;
	places: Record<string, MappedPlace>;
	today: Date | null;
	/** the day the Week program tab shows, 0 = Monday */
	day: number;
	todayIdx: number;
	selection: Selection | null;
	/** the camera reads its size to leave the room it takes free */
	panelRef: RefObject<HTMLDivElement>;
	onClose: () => void;
	/** a place's "After this" strip, or an offer of the base's sheet, was pressed: the page goes to that offer's card */
	onOffer: (id: string) => void;
	onTrack: (action: PartnerMapAction, item?: string) => void;
}

/** A touch this close to the sheet's top edge is the grab handle's: it drags the sheet even when the content is scrolled. */
const GRAB_PX = 56;
/** a touch moves this far before it is a drag: a tap's jitter is not one, and the browser has not yet taken the touch for its own scroll */
const SLOP_PX = 4;
/** only the last stretch of a drag says how fast the finger was going when it let go */
const FLICK_WINDOW_MS = 100;

/**
 * The detail sheet of the hostel, an event or a place: a card over the map on a wide screen, a sheet from the bottom
 * of the screen on a phone (drag it down, or press the cross, to close). It keeps what it showed while it slides away.
 */
const Sheet = ({ content, places, today, day, todayIdx, selection, panelRef, onClose, onOffer, onTrack }: SheetProps) => {
	const { partner, base, weekProgram, info, contact, offers, rideLabel, theme } = content;
	const t = strings(content.lang);
	const shown = useRef(selection);
	if (selection) shown.current = selection;
	const current = shown.current;
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	// Drag down to close, like a native bottom sheet: the sheet follows the finger and, on letting go, either goes or
	// comes back (the CSS transition does both, so reduced motion is honoured). A drag starts from the grab zone at the
	// sheet's top, or from the content when that is scrolled to the top; native listeners, since a React touchmove is
	// passive and could not stop the browser's own scroll.
	useEffect(() => {
		const el = panelRef.current;
		if (!el) return undefined;
		let start: { y: number; free: boolean } | null = null;
		let samples: { y: number; t: number }[] = [];
		let offset = 0;
		let dragging = false;

		const onStart = (e: TouchEvent) => {
			if (e.touches.length !== 1) {
				start = null;
				return;
			}
			const y = e.touches[0].clientY;
			start = { y, free: el.scrollTop <= 0 || y - el.getBoundingClientRect().top < GRAB_PX };
			samples = [{ y, t: e.timeStamp }];
			dragging = false;
		};
		const onMove = (e: TouchEvent) => {
			if (!start) return;
			const y = e.touches[0].clientY;
			if (!dragging) {
				// not a drag until it is a downward one that may be: otherwise the content scrolls
				if (!start.free || y - start.y <= SLOP_PX) return;
				dragging = true;
				el.style.transition = "none";
			}
			if (e.cancelable) e.preventDefault();
			offset = Math.max(0, y - start.y - SLOP_PX);
			el.style.transform = `translateY(${offset}px)`;
			samples.push({ y, t: e.timeStamp });
			while (samples.length > 2 && e.timeStamp - samples[0].t > FLICK_WINDOW_MS) samples.shift();
		};
		const onEnd = (e: TouchEvent) => {
			const wasDragging = dragging;
			dragging = false;
			start = null;
			if (!wasDragging) return;
			const first = samples[0];
			const last = samples[samples.length - 1];
			const rested = e.timeStamp - last.t > FLICK_WINDOW_MS || last.t === first.t;
			const velocity = rested ? 0 : (last.y - first.y) / (last.t - first.t);
			const close = e.type === "touchend" && shouldCloseSheet(offset, el.offsetHeight, velocity);
			el.style.transition = "";
			el.style.transform = "";
			if (close) onCloseRef.current();
		};

		el.addEventListener("touchstart", onStart, { passive: true });
		el.addEventListener("touchmove", onMove, { passive: false });
		el.addEventListener("touchend", onEnd);
		el.addEventListener("touchcancel", onEnd);
		return () => {
			el.removeEventListener("touchstart", onStart);
			el.removeEventListener("touchmove", onMove);
			el.removeEventListener("touchend", onEnd);
			el.removeEventListener("touchcancel", onEnd);
		};
	}, [panelRef]);

	// On a phone the hostel look's sheet starts at the map's bottom edge, so that no strip of the list shows between them, and its photo takes the room that is left
	// (the camera counts it there already, `sheetMinHeight` in MapScene: this only has to be there by the time it is seen)
	useEffect(() => {
		const el = panelRef.current;
		const map = el?.parentElement;
		if (!el || !map || !hostelLook(theme)) return undefined;
		const reach = () => {
			el.style.minHeight = window.innerWidth <= PHONE_MAX_WIDTH ? `${sheetMinHeight(window.innerHeight, map.getBoundingClientRect().bottom)}px` : "";
		};
		reach();
		window.addEventListener("resize", reach);
		return () => window.removeEventListener("resize", reach);
	}, [panelRef, selection, theme]);

	const close = (
		<button type="button" id="partnermap-sheet-close" className={styles.close} aria-label={t.close} onClick={onClose}>
			<UiIcon name="close" />
		</button>
	);

	let body: ReactNode = null;
	if (current?.kind === "base") {
		const atHostel = eventsOn(weekProgram, todayIdx, today).filter((e) => e.where === "hostel");
		body = (
			<>
				<div className={styles.hero}>
					<img src={base.photos[0]} alt={partner.name} />
					{close}
				</div>
				<div className={styles.bd}>
					<div className={styles.meta}>
						<span className={styles.lbl}>{base.kind ?? t.hostel}</span>
						{info?.address ?? base.address}
					</div>
					<h2>{partner.name}</h2>
					{info && theme.lit ? (
						<HostelPanel content={content} info={info} atHostel={atHostel} onTrack={onTrack} />
					) : contact ? (
						<VenuePanel content={content} contact={contact} onOffer={onOffer} onTrack={onTrack} />
					) : (
						<>
							{atHostel.length > 0 && (
								<div className={styles.events}>
									{atHostel.map((e) => (
										<div key={e.id}>
											<b>{e.title}</b>
											<span>
												{e.time}
												{e.price ? ` · ${e.price}` : ""}
											</span>
										</div>
									))}
								</div>
							)}
							{info && (
								<InfoLinks
									info={info}
									shortName={partner.shortName}
									idPrefix="partnermap-sheet-info"
									withAddress={false}
									lang={content.lang}
									onContact={(channel) => onTrack("contact", channel)}
								/>
							)}
							<div className={styles.acts}>
								{!info && (
									<a className={cx(styles.btn, styles.accent)} href={partner.url} target="_blank" rel="noopener" onClick={() => onTrack("contact", "book")}>
										{t.bookBed}
									</a>
								)}
								<a
									id="partnermap-start-planning"
									className={cx(styles.btn, styles.primary)}
									href={planningUrl(content)}
									target="_blank"
									rel="noopener"
									onClick={() => onTrack("start_planning")}
								>
									{t.startPlanning}
								</a>
							</div>
						</>
					)}
				</div>
			</>
		);
	} else if (current?.kind === "event" && weekProgram && today) {
		const e = weekProgram.events.find((x) => x.id === current.id)!;
		const venue = e.where === "hostel" ? null : places[e.where];
		const photo = e.photo ?? e.image ?? (venue ? venue.photo : base.photos[0]);
		body = (
			<>
				<div className={cx(styles.hero, styles.at)}>
					{photo ? <img src={photo} alt="" /> : <MarkerIcon name={e.icon} size={56} />}
					{close}
				</div>
				<div className={styles.bd}>
					<div className={styles.meta}>
						{e.time && <span className={cx(styles.lbl, styles.at)}>{e.time}</span>}
						{day === todayIdx ? t.today : t.days[day]}
						{e.date ? ` · ${t.oneNightOnly}` : ""}
						{e.price ? ` · ${e.price}` : ""}
					</div>
					<h2>{e.title}</h2>
					{e.note && <p>{/[.!?]$/.test(e.note) ? e.note : <>{e.note}.</>}</p>}
					<div className={styles.from}>
						<img src={partner.logo} alt="" />
						{venue ? `${venue.name} · ${walkLabel(venue.km, rideLabel, content.lang, content.bikeFromKm)}` : t.atHostel}
					</div>
					<div className={styles.acts}>
						{e.booking && (
							<a className={cx(styles.btn, styles.accent)} href={e.booking.url} target="_blank" rel="noopener" onClick={() => onTrack("tickets", e.id)}>
								{e.booking.cta}
							</a>
						)}
						{venue && (
							<a className={styles.btn} href={venue.googleUrl} target="_blank" rel="noopener" onClick={() => onTrack("place_map", venue.slug)}>
								<UiIcon name="directions" />
								{t.directions}
							</a>
						)}
						{e.signup && (
							<a
								className={cx(styles.btn, styles.whatsapp)}
								href={signupUrl(content, e, today, day)}
								target="_blank"
								rel="noopener"
								onClick={() => onTrack("whatsapp_signup", e.id)}
							>
								<UiIcon name="whatsapp" />
								{t.signUpWhatsapp}
							</a>
						)}
					</div>
					{e.signup && (
						<p className={styles.hint}>
							{t.orSignUp}{" "}
							<a href={`tel:${partner.tel}`} onClick={() => onTrack("contact", "call")}>
								{partner.telDisplay}
							</a>
						</p>
					)}
				</div>
			</>
		);
	} else if (current?.kind === "place") {
		const p = places[current.slug];
		const found = sisterOf(content);
		const sister = found && found.place.slug === p.slug ? found.sister : null;
		// the offer the place suggests for after the visit: its photo, its price, and the way back to the base
		const after = p.after ? offers?.cards.find((card) => card.id === p.after) : undefined;
		const afterPrice = after && fromPrice(after);
		body = (
			<>
				<div className={styles.hero}>
					{p.photo ? <img src={p.photo} alt={p.name} /> : <MarkerIcon name={p.icon} size={56} />}
					{close}
				</div>
				<div className={styles.bd}>
					<div className={styles.meta}>
						{sister ? (
							<>
								<span className={styles.lbl} style={{ background: sister.color, color: sister.ink ?? "#fff" }}>
									{t.sister}
								</span>
								{sister.address}
							</>
						) : (
							<span className={styles.lbl}>{p.label ?? t.venue}</span>
						)}
					</div>
					<h2>{p.name}</h2>
					{sister ? (
						<SisterPanel content={content} sister={sister} place={p} onTrack={onTrack} />
					) : (
						<>
							{p.blurb && <p>{p.blurb}</p>}
							<div className={styles.from}>
								<img src={partner.logo} alt="" />
								{t.walkFrom(p.reach ?? walkLabel(p.km, rideLabel, content.lang, content.bikeFromKm), partner.shortName)}
							</div>
							{after && (
								<button type="button" className={styles.after} onClick={() => onOffer(after.id)}>
									<img src={after.photo} alt="" />
									<span>
										<b>{t.afterThis(after.title)}</b>
										{isBike(p.km, content.bikeFromKm) ? t.minBackToBike(bikeMinutes(p.km), base.area) : t.minBackTo(walkMinutes(p.km), base.area)}
									</span>
									<u>{afterPrice ? t.fromPrice(afterPrice) : t.see}</u>
								</button>
							)}
							<div className={styles.acts}>
								<a className={styles.btn} href={p.googleUrl} target="_blank" rel="noopener" onClick={() => onTrack("place_map", p.slug)}>
									<UiIcon name="directions" />
									{t.directions}
								</a>
								{p.tickets ? (
									<a
										id="partnermap-tickets"
										className={cx(styles.btn, styles.primary)}
										href={p.tickets.url}
										target="_blank"
										rel="noopener sponsored"
										onClick={() => onTrack("tickets", p.slug)}
									>
										<MarkerIcon name="ticket" size={15} />
										{p.tickets.label}
									</a>
								) : content.cta.planUrl ? (
									<a
										id="partnermap-start-planning"
										className={cx(styles.btn, styles.primary)}
										href={content.cta.planUrl}
										target="_blank"
										rel="noopener"
										onClick={() => onTrack("start_planning", p.slug)}
									>
										<UiIcon name="save" />
										{t.savePlan}
									</a>
								) : (
									// interim: saves the map until the button's final job is decided (ClickUp 123yb69a2mr)
									<a
										id="partnermap-start-planning"
										className={cx(styles.btn, styles.primary)}
										href={placePlanningUrl(content, p.slug)}
										target="_blank"
										rel="noopener"
										onClick={() => onTrack("start_planning", p.slug)}
									>
										{t.startPlanning}
									</a>
								)}
							</div>
							{p.tickets && <p className={styles.via}>{p.tickets.note}</p>}
						</>
					)}
				</div>
			</>
		);
	}

	return (
		<div ref={panelRef} id="partnermap-sheet" className={cx(styles.sheet, selection && styles.open)} role="dialog" aria-label={t.details} aria-live="polite">
			{body}
		</div>
	);
};

export default Sheet;
